import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Timer, MousePointer2, Palette, Music, Grid3X3, Eye, Zap, 
  RotateCcw, Flame, Type, Headphones, PenTool, Search, 
  Focus, Mic2, FileText, Weight, ListOrdered, Layers, ShieldCheck,
  Trophy, Activity, ChevronRight, Globe, Brain, Keyboard,
  User, LogOut, X, Menu
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChronometerPage from "@/pages/ChronometerPage";
import PixelPrecisionPage from "@/pages/PixelPrecisionPage";
import BrainFrictionPage from "@/pages/BrainFrictionPage";
import SwiftKeysPage from "@/pages/SwiftKeysPage";
import VisualMemoryPage from "@/pages/VisualMemoryPage";
import MicroExpressionPage from "@/pages/MicroExpressionPage";
import { useMobileView } from "@/hooks/use-mobile-view";

import TwitchReflexPage from "@/pages/TwitchReflexPage";

import SpatialRotationPage from "@/pages/SpatialRotationPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Test data
const tests = [
  { id: 1, title: "The Human Chronometer", category: "Cognitive", icon: Timer, desc: "Stop the clock at exactly 10.000s.", color: "bg-indigo-600", route: "/test/chronometer" },
  { id: 2, title: "Pixel Perfect Precision", category: "Motor", icon: MousePointer2, desc: "Hit shrinking targets with zero margin for error.", color: "bg-emerald-600", route: "/test/pixel-precision" },
  { id: 3, title: "The Brain Friction", category: "Cognitive", icon: Brain, desc: "High-velocity Stroop effect with spatial interference.", color: "bg-purple-600", route: "/test/brain-friction" },
  { id: 4, title: "Swift Keys", category: "Motor", icon: Keyboard, desc: "High-performance typing speed and accuracy test.", color: "bg-pink-600", route: "/test/swift-keys" },
  { id: 5, title: "Visual Working Memory", category: "Memory", icon: Grid3X3, desc: "Pattern recall that scales to the impossible.", color: "bg-indigo-500", route: "/test/visual-memory" },
  { id: 6, title: "Micro-Expression Master", category: "Sensory", icon: Eye, desc: "Identify 0.1s facial shifts in high-res.", color: "bg-orange-600", route: "/test/micro-expression" },
  { id: 7, title: "The Twitch Reflex", category: "Motor", icon: Zap, desc: "The gold standard for pure reaction speed.", color: "bg-yellow-600", route: "/test/twitch-reflex" },
  { id: 8, title: "Spatial Rotation Pro", category: "Cognitive", icon: RotateCcw, desc: "Mental 3D manipulation against the clock.", color: "bg-cyan-600", route: "/test/spatial-rotation" },
  { id: 9, title: "The Pressure Cooker", category: "Cognitive", icon: Flame, desc: "Math and logic under extreme sensory distraction.", color: "bg-red-600", route: null },
  { id: 10, title: "Blind Type Racer", category: "Motor", icon: Type, desc: "Perfect transcription with a blank screen.", color: "bg-slate-600", route: null },
  { id: 11, title: "Frequency Ear Age", category: "Sensory", icon: Headphones, desc: "Benchmark your upper-limit hearing range.", color: "bg-violet-600", route: null },
  { id: 12, title: "Steady Hand Trace", category: "Motor", icon: PenTool, desc: "Navigate jagged paths without touching borders.", color: "bg-teal-600", route: null },
  { id: 13, title: "Pattern Recognition", category: "Cognitive", icon: Search, desc: "Find the odd fractal out in dense noise.", color: "bg-lime-600", route: null },
  { id: 14, title: "The Gaze Tracker", category: "Sensory", icon: Focus, desc: "AI-monitored focus and attention span.", color: "bg-rose-600", route: null },
  { id: 15, title: "Pitch Perfect Trainer", category: "Sensory", icon: Mic2, desc: "Absolute pitch identification without reference.", color: "bg-sky-600", route: null },
  { id: 16, title: "Scroll Speed Sensory", category: "Sensory", icon: FileText, desc: "Max comprehension reading at warp speeds.", color: "bg-amber-600", route: null },
  { id: 17, title: "Weight Guessing Lab", category: "Cognitive", icon: Weight, desc: "Visual mass estimation in variable gravity.", color: "bg-blue-700", route: null },
  { id: 18, title: "The Sequence Recall", category: "Memory", icon: ListOrdered, desc: "Simon-style endurance for the 1%.", color: "bg-emerald-700", route: null },
  { id: 19, title: "Multi-Task Reaction", category: "Motor", icon: Layers, desc: "Manage three sensory inputs simultaneously.", color: "bg-indigo-700", route: null },
  { id: 20, title: "The Silhouette Spotter", category: "Sensory", icon: ShieldCheck, desc: "Object recognition in high-noise environments.", color: "bg-gray-700", route: null },
];

const categories = ['All', 'Cognitive', 'Motor', 'Sensory', 'Memory'];

// Auth Dialog Component
function AuthDialog({ open, onOpenChange, onSuccess, pendingResult }) {
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authForm, setAuthForm] = useState({ username: '', password: '' });

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, authForm);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuthForm({ username: '', password: '' });
      toast.success(authMode === 'login' ? 'Welcome back!' : 'Account created successfully!');
      onSuccess(response.data.user);
      onOpenChange(false);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Authentication failed';
      const errorLower = errorMsg.toLowerCase();
      // If login fails with credential error, suggest creating account
      if (authMode === 'login' && (errorLower.includes('invalid') || errorLower.includes('not found'))) {
        setAuthMode('register');
        toast.info('Account not found. Create a new one?');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {pendingResult 
              ? `Sign in to save your result (±${pendingResult.toFixed(3)}s) and compete on the leaderboard!`
              : authMode === 'login' 
                ? 'Sign in to track your progress and compete globally.' 
                : 'Join thousands of users benchmarking their potential.'}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={authMode} onValueChange={(v) => setAuthMode(v)} className="mt-4">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger value="login" className="data-[state=active]:bg-indigo-600" data-testid="login-tab">Login</TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-indigo-600" data-testid="register-tab">Register</TabsTrigger>
          </TabsList>
          
          <form onSubmit={handleAuth} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input 
                id="username"
                type="text"
                placeholder="Enter username"
                value={authForm.username}
                onChange={(e) => setAuthForm(prev => ({ ...prev, username: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                required
                data-testid="username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input 
                id="password"
                type="password"
                placeholder="Enter password"
                value={authForm.password}
                onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                required
                data-testid="password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-indigo-600 hover:bg-indigo-500 font-heading uppercase tracking-wider py-5"
              disabled={authLoading}
              data-testid="auth-submit-btn"
            >
              {authLoading ? 'Processing...' : (authMode === 'login' ? 'Sign In' : 'Create Account')}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Landing Page Component
function LandingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingTestRoute, setPendingTestRoute] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [stats, setStats] = useState({ total_tests: 12400000, total_users: 142, best_chronometer_deviation: 0.002 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMobileView();
  
  // Hero Mini-Game State
  const [heroTime, setHeroTime] = useState(0);
  const [heroIsRunning, setHeroIsRunning] = useState(false);
  const [heroResult, setHeroResult] = useState(null);
  const [heroShowTimer, setHeroShowTimer] = useState(true);
  const [pendingHeroResult, setPendingHeroResult] = useState(null);
  const heroTimerRef = useRef(null);
  const heroStartTimeRef = useRef(null);
  const heroHideTimerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    fetchStats();
    
    // Cleanup timer on unmount
    return () => {
      if (heroTimerRef.current) clearInterval(heroTimerRef.current);
      if (heroHideTimerRef.current) clearTimeout(heroHideTimerRef.current);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/chronometer/stats`);
      
      // Add random number between 3 and 13 to actual user count
      const randomOffset = Math.floor(Math.random() * (13 - 3 + 1)) + 3;
      
      setStats(prev => ({
        ...prev,
        ...response.data,
        total_users: (response.data.total_users || 0) + randomOffset
      }));
    } catch (e) {
      console.log('Stats not available yet');
    }
  };

  // Hero Mini-Game Handlers
  const handleHeroChronometer = useCallback(() => {
    if (!heroIsRunning) {
      setHeroIsRunning(true);
      setHeroResult(null);
      setHeroShowTimer(true);
      setPendingHeroResult(null);
      heroStartTimeRef.current = Date.now();
      
      heroTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - heroStartTimeRef.current) / 1000;
        setHeroTime(elapsed);
      }, 10);
      
      // Hide timer after 3 seconds
      heroHideTimerRef.current = setTimeout(() => {
        setHeroShowTimer(false);
      }, 3000);
    } else {
      setHeroIsRunning(false);
      setHeroShowTimer(true);
      clearInterval(heroTimerRef.current);
      clearTimeout(heroHideTimerRef.current);
      const finalTime = (Date.now() - heroStartTimeRef.current) / 1000;
      setHeroTime(finalTime);
      const diff = Math.abs(10 - finalTime);
      setHeroResult(diff);
      
      // If user is not logged in, show auth dialog directly
      if (!user) {
        setPendingHeroResult(diff);
        setTimeout(() => setShowAuthDialog(true), 600);
      } else {
        // Auto-save for logged in users
        saveHeroResult(finalTime, diff);
      }
    }
  }, [heroIsRunning, user]);

  const saveHeroResult = async (finalTime, deviation) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/chronometer/results`, {
        score: finalTime,
        deviation: deviation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Result saved to your profile!');
      fetchStats();
    } catch (e) {
      toast.error('Failed to save result');
    }
  };

  const resetHeroGame = () => {
    setHeroTime(0);
    setHeroResult(null);
    setHeroIsRunning(false);
    setHeroShowTimer(true);
    setPendingHeroResult(null);
    if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    if (heroHideTimerRef.current) clearTimeout(heroHideTimerRef.current);
  };

  const getDeviationColor = (deviation) => {
    if (deviation < 0.05) return 'text-emerald-400';
    if (deviation < 0.1) return 'text-green-400';
    if (deviation < 0.3) return 'text-yellow-400';
    if (deviation < 0.5) return 'text-orange-400';
    return 'text-red-400';
  };

  const getResultMessage = (deviation) => {
    if (deviation < 0.01) return "SUPERHUMAN! Top 0.01%";
    if (deviation < 0.05) return "God-like timing!";
    if (deviation < 0.1) return "Pro level accuracy!";
    if (deviation < 0.3) return "Great reflexes!";
    if (deviation < 0.5) return "Good attempt!";
    return "Keep practicing!";
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const handleAuthSuccess = (newUser) => {
    setUser(newUser);
    
    // If there was a pending hero game result, save it
    if (pendingHeroResult !== null) {
      saveHeroResult(heroTime, pendingHeroResult);
      setPendingHeroResult(null);
    }
    
    if (pendingTestRoute) {
      navigate(pendingTestRoute);
      setPendingTestRoute(null);
    }
  };

  const handleTestClick = (test) => {
    if (!test.route) {
      toast.info('This test is coming soon!');
      return;
    }
    
    if (!user) {
      setPendingTestRoute(test.route);
      setShowAuthDialog(true);
    } else {
      navigate(test.route);
    }
  };

  const filteredTests = activeTab === 'All' ? tests : tests.filter(t => t.category === activeTab);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-[#020617]/80 nav-blur border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold text-xl tracking-tight">
            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center" data-testid="logo">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="hidden sm:inline">ATTRIBUTE<span className="text-indigo-500">LAB</span></span>
          </div>
          
          {/* Desktop nav */}
          <div className={`hidden ${isMobile ? '' : 'md:flex'} items-center gap-8 text-sm font-medium text-slate-400`}>
            <a href="#tests" className="hover:text-white transition-colors" data-testid="nav-tests">Tests</a>
            <a href="#" className="hover:text-white transition-colors" data-testid="nav-leaderboards">Leaderboards</a>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-slate-300" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    {user.username}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                  <DropdownMenuItem onClick={handleLogout} className="text-slate-300 focus:bg-slate-800 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => setShowAuthDialog(true)} 
                className="bg-indigo-600 text-white hover:bg-indigo-500 rounded-full px-5"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            className={`${isMobile ? 'flex' : 'md:hidden'}`} 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className={`${isMobile ? 'block' : 'md:hidden'} bg-slate-900 border-t border-slate-800 p-4 space-y-4`}>
            <a href="#tests" className="block text-slate-300 hover:text-white py-2">Tests</a>
            <a href="#" className="block text-slate-300 hover:text-white py-2">Leaderboards</a>
            {user ? (
              <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-300">
                <LogOut className="w-4 h-4 mr-2" /> Logout ({user.username})
              </Button>
            ) : (
              <Button onClick={() => { setShowAuthDialog(true); setMobileMenuOpen(false); }} className="w-full bg-indigo-600">
                Sign In
              </Button>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 hero-glow min-h-screen flex items-center">
        <div className={`max-w-7xl mx-auto grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-2'} gap-12 items-center w-full`}>
          <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              {stats.total_users > 0 ? `${stats.total_users.toLocaleString()} users testing right now` : 'Live testing platform'}
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight">
              Benchmark Your <br />
              <span className="gradient-text">Human Potential.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto lg:mx-0">
              The world's most precise laboratory for testing cognitive, motor, and sensory attributes. Proven by athletes, built for the obsessed.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
              <Button 
                className="px-6 sm:px-8 py-5 sm:py-6 bg-white text-slate-950 font-bold rounded-sm hover:bg-slate-200 text-base sm:text-lg font-heading uppercase tracking-wider"
                data-testid="explore-tests-btn"
                onClick={() => document.getElementById('tests')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore All Tests
              </Button>
              <Button 
                variant="outline"
                className="px-6 sm:px-8 py-5 sm:py-6 bg-transparent border-slate-700 text-white font-bold rounded-sm hover:bg-slate-800 text-base sm:text-lg font-heading uppercase tracking-wider"
                data-testid="view-rankings-btn"
              >
                View Rankings
              </Button>
            </div>
          </div>

          {/* Featured Test - Playable Mini-Game */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <div 
              className="relative bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-2xl"
              data-testid="hero-chronometer-card"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-heading font-bold text-slate-400 uppercase tracking-widest text-xs">
                  Try It Now: Human Chronometer
                </h3>
                <div className="flex gap-1">
                  {[1, 2, 3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>)}
                </div>
              </div>
              
              <div className="text-center py-6 sm:py-8">
                {/* Timer Display */}
                <div 
                  className={`font-mono text-6xl sm:text-7xl md:text-8xl font-bold tabular-nums transition-all duration-300 ${
                    !heroShowTimer && heroIsRunning 
                      ? 'opacity-0 blur-xl' 
                      : heroIsRunning 
                        ? 'text-white' 
                        : heroResult !== null 
                          ? getDeviationColor(heroResult) 
                          : 'text-slate-500'
                  }`}
                  data-testid="hero-timer-display"
                >
                  {heroShowTimer || !heroIsRunning ? heroTime.toFixed(3) : '?.???'}
                </div>
                <p className="text-slate-500 text-sm mt-2 mb-6">
                  {!heroShowTimer && heroIsRunning 
                    ? 'Timer hidden... stop at 10.000s!' 
                    : 'Stop the clock at exactly 10.000 seconds'}
                </p>
                
                {/* Game Controls */}
                <div className="flex gap-3 max-w-sm mx-auto">
                  <Button 
                    onClick={handleHeroChronometer}
                    className={`flex-1 py-5 sm:py-6 rounded-xl text-lg sm:text-xl font-heading font-bold uppercase tracking-wider transition-all ${
                      heroIsRunning 
                        ? 'bg-red-500 hover:bg-red-400 shadow-lg shadow-red-500/30' 
                        : 'bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-500/30'
                    }`}
                    data-testid="hero-start-stop-btn"
                  >
                    {heroIsRunning ? 'STOP' : 'START'}
                  </Button>
                  {(heroResult !== null || heroTime > 0) && !heroIsRunning && (
                    <Button 
                      onClick={resetHeroGame}
                      variant="outline"
                      className="py-5 sm:py-6 px-5 rounded-xl border-slate-700 hover:bg-slate-800"
                      data-testid="hero-reset-btn"
                    >
                      <RotateCcw className="w-5 h-5" />
                    </Button>
                  )}
                </div>

                {/* Result Display */}
                {heroResult !== null && (
                  <div className="mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="text-slate-400 text-xs uppercase tracking-widest mb-1 font-heading">Your Deviation</div>
                      <div className={`text-3xl sm:text-4xl font-mono font-bold ${getDeviationColor(heroResult)}`}>
                        ±{heroResult.toFixed(3)}s
                      </div>
                      <p className="text-slate-400 mt-2 text-sm">{getResultMessage(heroResult)}</p>
                    </div>
                  </div>
                )}

                {/* Status text */}
                {heroResult === null && !heroIsRunning && (
                  <p className="text-xs text-slate-500 mt-4">
                    {user ? 'Your results will be saved automatically' : 'No login required to play'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-12 border-y border-slate-800 bg-slate-900/50">
        <div className={`max-w-7xl mx-auto px-4 grid grid-cols-2 ${isMobile ? '' : 'md:grid-cols-4'} gap-6 sm:gap-8`}>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-heading font-bold stat-number" data-testid="stat-tests">
              {stats.total_tests > 1000000 
                ? `${(stats.total_tests / 1000000).toFixed(1)}M` 
                : stats.total_tests.toLocaleString()}
            </div>
            <div className="text-slate-500 text-xs sm:text-sm uppercase tracking-wider font-heading">Tests Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-heading font-bold stat-number" data-testid="stat-countries">142</div>
            <div className="text-slate-500 text-xs sm:text-sm uppercase tracking-wider font-heading">Countries</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-heading font-bold stat-number text-emerald-400" data-testid="stat-record">
              ±{stats.best_deviation?.toFixed(3) || '0.002'}s
            </div>
            <div className="text-slate-500 text-xs sm:text-sm uppercase tracking-wider font-heading">World Record</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-heading font-bold stat-number" data-testid="stat-latency">99.9%</div>
            <div className="text-slate-500 text-xs sm:text-sm uppercase tracking-wider font-heading">Accuracy</div>
          </div>
        </div>
      </section>

      {/* Main Grid */}
      <section id="tests" className="py-16 sm:py-24 px-4 bg-[#020617]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12 gap-6">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Select Your Test</h2>
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex gap-2 pb-2">
                  {categories.map(cat => (
                    <Button 
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      variant="ghost"
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all category-tab ${
                        activeTab === cat 
                          ? 'bg-indigo-600 text-white hover:bg-indigo-600' 
                          : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                      data-testid={`category-${cat.toLowerCase()}`}
                    >
                      {cat}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400 bg-slate-900 p-2.5 px-4 rounded-lg border border-slate-800">
              <Globe className="w-4 h-4 text-emerald-500" /> 
              <span>Global Competition Active</span>
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${isMobile ? '' : 'lg:grid-cols-4'} gap-4 sm:gap-6`}>
            {filteredTests.map((test) => {
              const IconComponent = test.icon;
              return (
                <div 
                  key={test.id} 
                  className="test-card group p-5 sm:p-6 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer relative overflow-hidden"
                  onClick={() => handleTestClick(test)}
                  data-testid={`test-card-${test.id}`}
                >
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-lg ${test.color} flex items-center justify-center mb-4 text-white`}>
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h3 className="font-heading font-bold text-base sm:text-lg mb-2 group-hover:text-indigo-400 transition-colors">{test.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-5 sm:mb-6 line-clamp-2">
                    {test.desc}
                  </p>
                  <div className="flex items-center text-xs font-heading font-bold text-slate-400 group-hover:text-white transition-colors uppercase tracking-wider">
                    {test.route ? 'Start Test' : 'Coming Soon'} 
                    <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Trophy className="w-12 h-12" />
                  </div>
                  {!test.route && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-slate-800 rounded text-xs text-slate-500 font-heading">
                      Soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 px-4 border-t border-slate-800 bg-[#020617]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-8">
          <div className="flex items-center gap-2 font-heading font-bold text-lg opacity-50">
            <Activity className="w-5 h-5" />
            <span>ATTRIBUTE<span className="text-indigo-500">LAB</span></span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-slate-500 text-sm">
            <a href="#" className="footer-link hover:text-white">Scientific Method</a>
            <a href="#" className="footer-link hover:text-white">API for Researchers</a>
            <a href="#" className="footer-link hover:text-white">Privacy</a>
            <a href="#" className="footer-link hover:text-white">Terms</a>
          </div>
          <div className="text-slate-600 text-xs">
            © 2024 Attribute Lab. Accuracy depends on hardware latency.
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <AuthDialog 
        open={showAuthDialog} 
        onOpenChange={(open) => {
          setShowAuthDialog(open);
          if (!open) setPendingHeroResult(null);
        }}
        onSuccess={handleAuthSuccess}
        pendingResult={pendingHeroResult}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors theme="dark" />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/test/chronometer" element={<ChronometerPage />} />
        <Route path="/test/pixel-precision" element={<PixelPrecisionPage />} />
        <Route path="/test/brain-friction" element={<BrainFrictionPage />} />
        <Route path="/test/swift-keys" element={<SwiftKeysPage />} />
        <Route path="/test/visual-memory" element={<VisualMemoryPage />} />
        <Route path="/test/micro-expression" element={<MicroExpressionPage />} />
        <Route path="/test/twitch-reflex" element={<TwitchReflexPage />} />
        <Route path="/test/spatial-rotation" element={<SpatialRotationPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
